import { OBJECT_KINDS, type ObjectKind, type World } from '@tama/game-core';
import { type DevState, type DevTool, describeKind } from '../game/devWorld';
import { TvButton } from '../tv';

const TOOLS: { tool: DevTool; label: string; hint: string }[] = [
  { tool: 'wall', label: 'Wall', hint: 'Drag to paint walls.' },
  { tool: 'floor', label: 'Floor', hint: 'Drag to clear walls back to floor.' },
  { tool: 'move', label: 'Move', hint: 'Drag furniture. It keeps the corner you grabbed.' },
  { tool: 'place', label: 'Place', hint: 'Click to drop the piece selected below.' },
  { tool: 'erase', label: 'Remove', hint: 'Click a piece to take it out of the room.' },
  { tool: 'spawn', label: 'Spawn', hint: 'Click where the pet should start.' },
];

export interface DevPanelProps {
  world: World;
  dev: DevState;
  /** True while the draft differs from what the server has. */
  dirty: boolean;
  saving: boolean;
  onToolChange: (tool: DevTool) => void;
  onPlaceKindChange: (kind: ObjectKind) => void;
  onSave: () => void;
  onRevert: () => void;
  onReset: () => void;
  onClose: () => void;
}

/**
 * The world editor's controls.
 *
 * Web only — see `DEV_MODE_ENABLED`. Everything it does goes through
 * `applyDevPointer`, so this file holds no editing rules at all: it picks a
 * tool, and the reducer decides what a click with that tool means.
 */
export function DevPanel({
  world,
  dev,
  dirty,
  saving,
  onToolChange,
  onPlaceKindChange,
  onSave,
  onRevert,
  onReset,
  onClose,
}: DevPanelProps) {
  const hint = TOOLS.find((entry) => entry.tool === dev.tool)?.hint ?? '';

  return (
    <aside className="dev-panel" data-testid="dev-panel">
      <header className="dev-panel__head">
        <h2 className="dev-panel__title">World editor</h2>
        <TvButton variant="ghost" data-testid="dev-close" onClick={onClose}>
          Done
        </TvButton>
      </header>

      <section className="dev-panel__group">
        <h3 className="dev-panel__group-title">Tool</h3>
        <div className="dev-panel__buttons">
          {TOOLS.map(({ tool, label }) => (
            <TvButton
              key={tool}
              variant="ghost"
              aria-pressed={dev.tool === tool}
              data-testid={`dev-tool-${tool}`}
              onClick={() => onToolChange(tool)}
            >
              {label}
            </TvButton>
          ))}
        </div>
        <p className="dev-panel__hint" data-testid="dev-hint">
          {hint}
        </p>
      </section>

      <section className="dev-panel__group">
        <h3 className="dev-panel__group-title">Furniture</h3>
        <div className="dev-panel__buttons">
          {OBJECT_KINDS.map((kind) => (
            <TvButton
              key={kind}
              variant="ghost"
              aria-pressed={dev.placeKind === kind}
              data-testid={`dev-kind-${kind}`}
              onClick={() => onPlaceKindChange(kind)}
            >
              {describeKind(kind)}
            </TvButton>
          ))}
        </div>
      </section>

      <section className="dev-panel__group">
        <h3 className="dev-panel__group-title">Map</h3>
        <p className="dev-panel__readout" data-testid="dev-readout">
          {world.width}×{world.height} · {world.objects.length} objects · spawn {world.spawn.x},
          {world.spawn.y} · {dirty ? 'unsaved changes' : 'saved'}
        </p>
        <div className="dev-panel__buttons">
          <TvButton
            data-testid="dev-save"
            aria-disabled={!dirty || saving}
            onClick={() => {
              if (dirty && !saving) onSave();
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </TvButton>
          <TvButton
            variant="ghost"
            data-testid="dev-revert"
            aria-disabled={!dirty}
            onClick={() => {
              if (dirty) onRevert();
            }}
          >
            Revert
          </TvButton>
          <TvButton variant="danger" data-testid="dev-reset" onClick={onReset}>
            Reset map
          </TvButton>
        </div>
      </section>
    </aside>
  );
}
